/**
 * @license
 * Copyright 2024 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
export { eventsAsHarnessRunResults, sequenceEntryToHarnessRunResult };
export const eventIdFromEntryId = (entryId) => {
    return `e-${entryId || "0"}`;
};
export const entryIdFromEventId = (eventId) => {
    return eventId?.startsWith("e-") ? eventId.substring(2) : null;
};
export const idFromPath = (path) => {
    return path.join("-");
};
export const pathFromId = (id) => {
    return id.length ? id.split("-").map((s) => parseInt(s, 10)) : [];
};
function sequenceEntryToHarnessRunResult(entry, trimPath = (path) => path) {
    const [type, data] = entry;
    switch (type) {
        case "graphstart": {
            const { graphStart, path, graph: inspectableGraph, edges } = data;
            const graph = inspectableGraph?.raw();
            const graphId = inspectableGraph?.graphId() || "";
            return {
                type,
                data: {
                    timestamp: graphStart,
                    graphId,
                    path: trimPath(path),
                    graph,
                    edges,
                },
                async reply() { },
            };
        }
        case "nodestart": {
            const { path } = data;
            const { node, inputs, start: timestamp, traversalResult, } = data.event;
            return {
                type,
                data: {
                    node: node.descriptor,
                    inputs,
                    path: trimPath(path),
                    timestamp,
                },
                result: traversalResult,
                async reply() { },
            };
        }
        case "nodeend": {
            const { path } = data;
            const { node, inputs, outputs, end: timestamp, traversalResult, } = data.event;
            return {
                type,
                data: {
                    node: node.descriptor,
                    inputs,
                    outputs: outputs || {},
                    path: trimPath(path),
                    timestamp: timestamp || 0,
                    newOpportunities: traversalResult?.newOpportunities || [],
                },
                async reply() { },
            };
        }
        case "graphend": {
            const { path } = data;
            const { graphEnd: timestamp } = data;
            return {
                type,
                data: { path: trimPath(path), timestamp: timestamp || 0 },
                async reply() { },
            };
        }
        case "error": {
            const { graphStart: timestamp } = data;
            return {
                type,
                data: { error: "TODO: Store actual error object", timestamp },
                async reply() { },
            };
        }
        case "input": {
            const { path } = data;
            const { node, inputs: inputArguments, bubbled, start: timestamp, } = data.event;
            return {
                type,
                data: {
                    node: node.descriptor,
                    inputArguments,
                    bubbled,
                    path: trimPath(path),
                    timestamp,
                },
                async reply() { },
            };
        }
        case "output": {
            const { path } = data;
            const { node, outputs, bubbled, start: timestamp, } = data.event;
            return {
                type,
                data: {
                    node: node.descriptor,
                    outputs: outputs || {},
                    bubbled,
                    path: trimPath(path),
                    timestamp,
                },
                async reply() { },
            };
        }
        case "secret": {
            return null;
        }
        default: {
            throw new Error("Unknown event type: " + type);
        }
    }
}
// TODO: Use sequenceEntryToHarnessRunResult
async function* eventsAsHarnessRunResults(events) {
    const first = events[0];
    let endPath = [];
    if (first[0] !== "graphstart") {
        throw new Error("Expected a graphstart event at the beginning of the timeline");
    }
    else {
        endPath = first[1].path;
    }
    for await (const result of events) {
        const [type, data] = result;
        switch (type) {
            case "graphstart": {
                const { graphStart, path, graph: inspectableGraph, edges } = data;
                const graph = inspectableGraph?.mainGraphDescriptor();
                const graphId = inspectableGraph?.graphId() || "";
                yield {
                    type,
                    data: {
                        timestamp: graphStart,
                        path: trimPath(path),
                        graph,
                        graphId,
                        edges,
                    },
                    async reply() { },
                };
                break;
            }
            case "nodestart": {
                const { path } = data;
                const { node, inputs, start: timestamp, traversalResult, } = data.event;
                yield {
                    type,
                    data: {
                        node: node.descriptor,
                        inputs,
                        path: trimPath(path),
                        timestamp,
                    },
                    result: traversalResult,
                    async reply() { },
                };
                break;
            }
            case "nodeend": {
                const { path } = data;
                const { node, inputs, outputs, end: timestamp, traversalResult, } = data.event;
                yield {
                    type,
                    data: {
                        node: node.descriptor,
                        inputs,
                        outputs: outputs || {},
                        path: trimPath(path),
                        timestamp: timestamp || 0,
                        newOpportunities: traversalResult?.newOpportunities || [],
                    },
                    async reply() { },
                };
                break;
            }
            case "graphend": {
                const { path } = data;
                const { graphEnd: timestamp } = data;
                yield {
                    type,
                    data: { path: trimPath(path), timestamp: timestamp || 0 },
                    async reply() { },
                };
                if (path.join(".") === endPath.join(".")) {
                    return;
                }
                break;
            }
            case "error": {
                const { graphStart: timestamp } = data;
                yield {
                    type,
                    data: { error: "TODO: Store actual error object", timestamp },
                    async reply() { },
                };
                break;
            }
            case "input": {
                const { path } = data;
                const { node, inputs: inputArguments, bubbled, start: timestamp, } = data.event;
                yield {
                    type,
                    data: {
                        node: node.descriptor,
                        inputArguments,
                        bubbled,
                        path: trimPath(path),
                        timestamp,
                    },
                    async reply() { },
                };
                break;
            }
            case "output": {
                const { path } = data;
                const { node, outputs, bubbled, start: timestamp, } = data.event;
                yield {
                    type,
                    data: {
                        node: node.descriptor,
                        outputs: outputs || {},
                        bubbled,
                        path: trimPath(path),
                        timestamp,
                    },
                    async reply() { },
                };
                break;
            }
            case "secret": {
                break;
            }
            default: {
                throw new Error("Unknown event type: " + type);
            }
        }
    }
    function trimPath(path) {
        return path.slice(endPath.length);
    }
}
//# sourceMappingURL=conversions.js.map